import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  ApiTypesEnum,
  FedexAccountOutDTO,
  FedexAvailableProductDTO,
  FedexAvailableProductsRequestDTO,
  FedexPackageDTO,
  FedexPickupAvailabilityOutDTO,
  OpeningModeEnum,
} from '@api/ringil-api.v2';
import { Carrier } from '@components/RingilComponents/CarrierDistributionSelector/CarrierDistributionSelector.tsx';
import { ApiContext } from '@api/api.tsx';
import { Field } from '@components/framework/formfield/Field.tsx';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import InputCountry from '@components/framework/input/InputCountry/InputCountry.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import FedexDisclaimer from '@components/RingilComponents/FedexDisclaimer.tsx';
import FedexLogo from '@components/RingilComponents/FedexLogo.tsx';
import {
  dayjsFromApiUtc,
  formatDateTimeFromDayjsInTzWithLocale,
} from '@utils/dateWithTz.tsx';
import { InputCheckbox } from '@components/ringil3/Input/InputCheckbox/InputCheckbox.tsx';
import { ToastContext } from '@components/auth/ToastContext.tsx';
import { useDebounce } from 'use-debounce';
import FedexPickupForm, { FedexPickupValue, timeslotLocalDay, trimToHHmm } from '@components/RingilComponents/FedexPickupForm.tsx';
import { fedexAccountLabel } from '@components/RingilComponents/fedex/fedexAccountLabel.ts';

interface ComponentProps {
  values: Carrier[];
  onSelect: (values: Carrier[]) => void;
  productParameters: FedexAvailableProductsRequestDTO;
  pickupTimezoneId?: string;
}

// A package is valid only when all of its required numeric fields are provided and positive.
// Missing values (e.g. an empty amount) make the FedEx endpoint return an error, so we skip the call.
const isValidNumber = (value?: number): boolean => typeof value === 'number' && !isNaN(value) && value > 0;

const isValidPackage = (pkg?: FedexPackageDTO): boolean =>
  !!pkg && isValidNumber(pkg.amount) && isValidNumber(pkg.weight) && isValidNumber(pkg.length) && isValidNumber(pkg.width) && isValidNumber(pkg.height);

// Only the non-optional fields of FedexAvailableProductsRequestDTO are mandatory for the request.
// Without all of them FedEx cannot resolve services, so we skip the call entirely.
const hasMandatoryParameters = (p?: FedexAvailableProductsRequestDTO): boolean =>
  !!p &&
  !!p.countryFrom &&
  !!p.countryTo &&
  !!p.postalCodeFrom &&
  !!p.postalCodeTo &&
  !!p.cityFrom &&
  !!p.cityTo &&
  Array.isArray(p.allPackages) &&
  p.allPackages.length > 0 &&
  p.allPackages.every(isValidPackage);

export const FedexProductTerms: React.FC<ComponentProps> = ({
                                                              values,
                                                              onSelect,
                                                              productParameters,
                                                              pickupTimezoneId,
                                                            }) => {


  const { tr } = useTranslationLgs();
  const [productTypes, setProductTypes] = useState<FedexAvailableProductDTO[]>([]);
  const { getFedexAvailableProducts, getFedexPickupAvailability, listFedexAccounts } = useContext(ApiContext);
  const { showToastMessage } = useContext(ToastContext);
  const [isFedex, setIsFedex] = useState<boolean>(false);
  const [fedexCarriers, setFedexCarriers] = useState<Carrier[]>([]);
  const [validationError, setValidationError] = useState<{ message: string; params?: string[] } | null>(null);
  // FedEx accounts allowed for the route (pickup location narrows, then delivery, else all company accounts)
  const [accounts, setAccounts] = useState<FedexAccountOutDTO[] | null>(null);

  // The parent rebuilds productParameters as a fresh object literal on every render, so depending on its
  // reference would re-request FedEx on every render (even when unrelated data such as cargo type changes).
  // Memoize by serialized value so the request only fires when a relevant field actually changes.
  const productParametersKey = JSON.stringify(productParameters ?? {});
  const stableProductParameters = useMemo(() => productParameters, [productParametersKey]);
  // Debounce so rapid edits (typing an address, adjusting packages) collapse into a single request
  // instead of firing one call per keystroke.
  const [debouncedProductParameters] = useDebounce(stableProductParameters, 500);

  useEffect(() => {
    const hasFedexApi = Array.isArray(values) && values.some(carrier =>
      Array.isArray(carrier.apiTypes) && carrier.apiTypes.includes(ApiTypesEnum.FEDEX),
    );

    setIsFedex(hasFedexApi);

    const fedexCarriers = Array.isArray(values)
      ? values.filter(carrier =>
        Array.isArray(carrier.apiTypes) && carrier.apiTypes.includes(ApiTypesEnum.FEDEX),
      )
      : [];
    setFedexCarriers(fedexCarriers);

  }, [values]);

  const fromCompanyLocationId = productParameters?.fromCompanyLocationId;
  const toCompanyLocationId = productParameters?.toCompanyLocationId;
  useEffect(() => {
    if (!isFedex) return;
    listFedexAccounts(fromCompanyLocationId ?? undefined, toCompanyLocationId ?? undefined, data => setAccounts(data));
  }, [isFedex, fromCompanyLocationId, toCompanyLocationId]);

  const pickedAccountId = fedexCarriers[0]?._fedexAccountId;
  // one allowed account is used without asking; with several the user has to pick (no default)
  const effectiveAccountId =
    accounts?.length === 1 ? accounts[0].id : accounts?.some(a => a.id === pickedAccountId) ? pickedAccountId : undefined;
  const accountRequired = (accounts?.length ?? 0) > 1;
  const accountReady = accounts != null && !!effectiveAccountId;

  // keep the carrier in sync: the auto-picked account is saved, a no longer allowed one is dropped
  useEffect(() => {
    if (!isFedex || accounts == null || fedexCarriers.length === 0) return;
    if (fedexCarriers.some(c => c._fedexAccountId !== effectiveAccountId || !!c._fedexAccountRequired !== accountRequired)) {
      updateFedexCarriers(item => ({ ...item, _fedexAccountId: effectiveAccountId, _fedexAccountRequired: accountRequired }));
    }
  }, [isFedex, accounts, fedexCarriers, effectiveAccountId, accountRequired]);

  useEffect(() => {
    if (!accountReady) {
      setProductTypes([]);
      return;
    }
    if (isFedex && hasMandatoryParameters(debouncedProductParameters)) {
      getFedexAvailableProducts(
        { ...debouncedProductParameters, fedexAccountId: effectiveAccountId },
        values => {
          setValidationError(null);
          setProductTypes(values);
          const availableServiceTypes = values.map(p => p.serviceType);

          setFedexCarriers(prev => {
            const updated = prev.map(item => {
              const stillAvailable = availableServiceTypes.includes(item._fedexProductTypeCode);
              const serviceType = stillAvailable ? item._fedexProductTypeCode : values[0]?.serviceType;
              return {
                ...item,
                _fedexProductTypeCode: serviceType,
              } as Carrier;
            });

            if (JSON.stringify(updated) !== JSON.stringify(prev)) {
              onSelect(updated);
            }

            return updated;
          });
        },
        null,
        {
          onValidationFailed: (response) => {
            if (response?.length > 0) {
              const fedexMessage = response[0];
              if (fedexMessage) {
                setValidationError({
                  message: fedexMessage.message,
                  params: fedexMessage.params,
                });
              } else {
                setValidationError({
                  message: 'FedEx validation error occurred',
                  params: [],
                });
              }
            }
          },
          onError: (error) => {
            showToastMessage(
              tr('api.errorApi', 'Error'),
              <>
                <div className="toast-wrapper">
                  <div>{error.message}</div>
                  <div>
                    <strong>getFedexAvailableProducts</strong> - {error?.config?.method} - {error?.config?.url}
                  </div>
                </div>
              </>,
              'error',
              5000,
            );
          },
        }
      );
    }
  }, [isFedex, debouncedProductParameters, accountReady, effectiveAccountId]);

  const updateFedexCarriers = (mutator: (carrier: Carrier) => Carrier) => {
    const updated = fedexCarriers.map(mutator);
    onSelect(updated);
  };

  const handleApiProductSelect = (_fedexProductTypeCode: string) => {
    updateFedexCarriers(item => ({
      ...item,
      _fedexProductTypeCode,
    }));
  };

  const selectedServiceType = fedexCarriers[0]?._fedexProductTypeCode || (productTypes && productTypes[0]?.serviceType);
  const selectedProduct = productTypes?.find(pt => pt.serviceType === selectedServiceType);

  const courierRequested = fedexCarriers[0]?._fedexCourierRequested ?? true;
  const readyTime = trimToHHmm(fedexCarriers[0]?._fedexPickupReadyTime);
  const closeTime = trimToHHmm(fedexCarriers[0]?._fedexPickupCloseTime);
  const pickupDate = fedexCarriers[0]?._fedexPickupDate;
  const payerType = fedexCarriers[0]?._fedexPayerType ?? 'SENDER';
  const payerAccountNumber = fedexCarriers[0]?._fedexPayerAccountNumber ?? '';
  const payerCountry = fedexCarriers[0]?._fedexPayerCountry ?? '';
  const declaredValue = fedexCarriers[0]?._fedexDeclaredValue ?? null;
  const declaredValueCurrency = fedexCarriers[0]?._fedexDeclaredValueCurrency ?? '';
  const signatureOption = fedexCarriers[0]?._fedexSignatureOption ?? null;
  const packagingType = fedexCarriers[0]?._fedexPackagingType ?? 'YOUR_PACKAGING';
  const bookingConfirmationNumber = fedexCarriers[0]?._fedexBookingConfirmationNumber ?? '';
  const isFreightService = !!selectedServiceType?.includes('_FREIGHT');

  // pickup can be on any open day of the loading location's timeslots that FedEx offers
  const openDays = useMemo(
    () =>
      (debouncedProductParameters?.fromDeliverySlot?.days ?? [])
        .filter(day => day.openingMode !== OpeningModeEnum.CLOSED)
        .map(day => timeslotLocalDay(day))
        .filter(Boolean),
    [debouncedProductParameters],
  );

  // FedEx pickup availability is per address, date and service - re-fetched on every change
  const availabilityKey = JSON.stringify([
    debouncedProductParameters?.countryFrom,
    debouncedProductParameters?.countryTo,
    debouncedProductParameters?.postalCodeFrom,
    debouncedProductParameters?.cityFrom,
    debouncedProductParameters?.streetFrom,
    selectedServiceType,
    effectiveAccountId,
  ]);

  const loadPickupAvailability = (
    date: string,
    onData: (data: FedexPickupAvailabilityOutDTO) => void,
    onFailed: () => void,
  ) => {
    if (!hasMandatoryParameters(debouncedProductParameters) || !accountReady) return;
    getFedexPickupAvailability(
      {
        countryFrom: debouncedProductParameters.countryFrom,
        countryTo: debouncedProductParameters.countryTo,
        postalCodeFrom: debouncedProductParameters.postalCodeFrom,
        cityFrom: debouncedProductParameters.cityFrom,
        streetFrom: debouncedProductParameters.streetFrom,
        dispatchDate: date,
        serviceType: selectedServiceType,
        fedexAccountId: effectiveAccountId,
        fromCompanyLocationId: debouncedProductParameters.fromCompanyLocationId,
        toCompanyLocationId: debouncedProductParameters.toCompanyLocationId,
      },
      onData,
      null,
      {
        onError: (error) => {
          onFailed();
          showToastMessage(
            tr('api.errorApi', 'Error'),
            <>
              <div className="toast-wrapper">
                <div>{error.message}</div>
                <div>
                  <strong>getFedexPickupAvailability</strong> - {error?.config?.method} - {error?.config?.url}
                </div>
              </div>
            </>,
            'error',
            5000,
          );
        },
      },
    );
  };

  // the courier is ordered by default - IMI books a pickup with every FedEx shipment
  useEffect(() => {
    if (isFedex && fedexCarriers.some(c => c._fedexCourierRequested === undefined)) {
      updateFedexCarriers(item => ({ ...item, _fedexCourierRequested: item._fedexCourierRequested ?? true }));
    }
  }, [isFedex, fedexCarriers]);

  const handleToggleCourier = (next: boolean) => {
    updateFedexCarriers(item => ({
      ...item,
      _fedexCourierRequested: next,
      ...(next ? {} : { _fedexPickupDate: undefined, _fedexPickupReadyTime: undefined, _fedexPickupCloseTime: undefined }),
    }));
  };

  const handlePickupChange = (value: FedexPickupValue) => {
    updateFedexCarriers(item => ({
      ...item,
      _fedexPickupDate: value.pickupDate,
      _fedexPickupReadyTime: value.readyTime,
      _fedexPickupCloseTime: value.closeTime,
    }));
  };

  const bookingError = useMemo(() => {
    if (!isFreightService) return null;
    if (!bookingConfirmationNumber?.trim()) {
      return tr('FedexProductTerms.bookingConfirmationRequired', 'FedEx booking confirmation number is required for freight services');
    }
    return null;
  }, [isFreightService, bookingConfirmationNumber, tr]);

  return (
    <>
      {isFedex && (
        <div className="p-4 rounded-lg bg-base-tertiary gap-4 flex flex-col">
          <FedexLogo height={40} className="self-start" />
          {accounts != null && accounts.length === 0 && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800">
              {tr('FedexProductTerms.noAccount', 'No FedEx account is connected for this route. Connect one in Company profile → FedEx.')}
            </div>
          )}
          {accounts != null && accounts.length === 1 && (
            <div>
              <div className="text-sm font-medium text-content-primary">{tr('FedexProductTerms.account', 'FedEx account')}</div>
              <span className="text-content-secondary" data-testid={'fedex-account-readonly'}>{fedexAccountLabel(accounts[0])}</span>
            </div>
          )}
          {accountRequired && (
            <div className="flex flex-col gap-1">
              <Field label={tr('FedexProductTerms.account', 'FedEx account')} required>
                <Dropdown
                  options={accounts.map(a => ({ label: fedexAccountLabel(a), value: a.id }))}
                  optionLabel="label"
                  optionValue="value"
                  value={effectiveAccountId ?? null}
                  onChange={e => updateFedexCarriers(item => ({ ...item, _fedexAccountId: e.value }))}
                  style={{ width: '100%' }}
                  placeholder={tr('FedexProductTerms.accountPlaceholder', 'Select FedEx account')}
                  data-testid={'fedex-account-dropdown'}
                />
              </Field>
              {!effectiveAccountId && (
                <small className="text-content-secondary">
                  {tr('FedexProductTerms.accountRequired', 'Select the FedEx account the shipment goes on')}
                </small>
              )}
            </div>
          )}
          {!accountReady ? null : validationError ? (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <div className="text-sm font-medium text-red-800">
                {tr('FedexProductTerms.validationError', 'FedEx Validation Error')}
              </div>
              <div className="text-sm text-red-700 mt-2">
                <div className="font-medium">{validationError.message}</div>
                {validationError.params && validationError.params.length > 0 && (
                  <div className="mt-2 text-xs">
                    {validationError.params.map((param, index) => (
                      <div key={index} className="mt-1">{param}</div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              <Field
                label={tr('FedexProductTerms.fedexProduct', 'FedEx - select service')}
                required
              >
                <Dropdown
                  options={productTypes?.map(pt => ({
                    label: pt.serviceName ?? pt.serviceType,
                    value: pt.serviceType,
                  }))}
                  optionLabel="label"
                  optionValue="value"
                  value={selectedServiceType}
                  onChange={e => handleApiProductSelect(e.value)}
                  style={{ width: '100%' }}
                  placeholder={tr('FedexProductTerms.fedexProductPlaceholder', 'Select service')}
                />
              </Field>

              {selectedProduct?.price && (
                <div>
                  <div className="text-sm font-medium text-content-primary">
                    {tr('FedexProductTerms.price', 'Price')}
                  </div>
                  <span className="text-content-secondary">
                    {selectedProduct.price.price} {selectedProduct.price.currency}
                  </span>
                </div>
              )}

              {selectedProduct?.estimatedDeliveryDate && (
                <div>
                  <div className="text-sm font-medium text-content-primary">
                    {tr('FedexProductTerms.estimatedDeliveryDate', 'Estimated Delivery Date')}
                  </div>
                  <span className="text-content-secondary">
                    {formatDateTimeFromDayjsInTzWithLocale(dayjsFromApiUtc(selectedProduct.estimatedDeliveryDate), "UTC")}
                  </span>
                </div>
              )}

              {!selectedProduct?.estimatedDeliveryDate && selectedProduct?.transitDaysDescription && (
                <div>
                  <div className="text-sm font-medium text-content-primary">
                    {tr('FedexProductTerms.transitDays', 'Transit time')}
                  </div>
                  <span className="text-content-secondary">
                    {selectedProduct.transitDaysDescription}
                  </span>
                </div>
              )}

              <div className="flex flex-col gap-3 mt-2">
                <InputCheckbox
                  label={tr('FedexProductTerms.courierRequested', 'Order FedEx courier pickup')}
                  value={courierRequested}
                  onChange={(_, value) => handleToggleCourier(!!value)}
                />

                {courierRequested && (
                  <FedexPickupForm
                    openDays={openDays}
                    loadAvailability={loadPickupAvailability}
                    availabilityKey={availabilityKey}
                    value={{ pickupDate: pickupDate, readyTime, closeTime }}
                    onChange={handlePickupChange}
                    timezoneMissing={!pickupTimezoneId}
                  />
                )}
              </div>

              <div className="flex flex-col gap-3 mt-2">
                <Field label={tr('FedexProductTerms.packagingType', 'Packaging')}>
                  <Dropdown
                    options={[
                      { label: tr('FedexProductTerms.packagingType.YOUR_PACKAGING', 'Your packaging'), value: 'YOUR_PACKAGING' },
                      { label: 'FedEx Envelope', value: 'FEDEX_ENVELOPE' },
                      { label: 'FedEx Pak', value: 'FEDEX_PAK' },
                      { label: 'FedEx Box', value: 'FEDEX_BOX' },
                      { label: 'FedEx Tube', value: 'FEDEX_TUBE' },
                    ]}
                    optionLabel="label"
                    optionValue="value"
                    value={packagingType}
                    onChange={e => updateFedexCarriers(item => ({ ...item, _fedexPackagingType: e.value }))}
                    style={{ width: '100%' }}
                  />
                </Field>

                <Field label={tr('FedexProductTerms.payerType', 'Transport charges paid by')}>
                  <Dropdown
                    options={[
                      { label: tr('FedexProductTerms.payerType.SENDER', 'Sender'), value: 'SENDER' },
                      { label: tr('FedexProductTerms.payerType.RECIPIENT', 'Recipient'), value: 'RECIPIENT' },
                      { label: tr('FedexProductTerms.payerType.THIRD_PARTY', 'Third party'), value: 'THIRD_PARTY' },
                    ]}
                    optionLabel="label"
                    optionValue="value"
                    value={payerType}
                    onChange={e => updateFedexCarriers(item => ({ ...item, _fedexPayerType: e.value }))}
                    style={{ width: '100%' }}
                  />
                </Field>

                {payerType !== 'SENDER' && (
                  <div className="flex flex-row gap-3">
                    <Field label={tr('FedexProductTerms.payerAccountNumber', 'Payer FedEx account number')} required>
                      <InputText
                        value={payerAccountNumber}
                        onChange={e => updateFedexCarriers(item => ({ ...item, _fedexPayerAccountNumber: e.target.value }))}
                        maxLength={9}
                      />
                    </Field>
                    <Field label={tr('FedexProductTerms.payerCountry', 'Payer country')} required>
                      <InputCountry
                        value={payerCountry}
                        onChange={v => updateFedexCarriers(item => ({ ...item, _fedexPayerCountry: v }))}
                      />
                    </Field>
                  </div>
                )}

                <div className="flex flex-row gap-3">
                  <Field label={tr('FedexProductTerms.declaredValue', 'Declared value per package')}>
                    <InputNumber
                      value={declaredValue}
                      onChange={e => updateFedexCarriers(item => ({ ...item, _fedexDeclaredValue: e.value ?? undefined }))}
                      minFractionDigits={0}
                      maxFractionDigits={2}
                    />
                  </Field>
                  <Field label={tr('FedexProductTerms.declaredValueCurrency', 'Currency')}>
                    <InputText
                      value={declaredValueCurrency}
                      onChange={e => updateFedexCarriers(item => ({ ...item, _fedexDeclaredValueCurrency: e.target.value.toUpperCase() }))}
                      maxLength={3}
                      style={{ width: '90px' }}
                    />
                  </Field>
                </div>

                <Field label={tr('FedexProductTerms.signatureOption', 'Delivery signature')}>
                  <Dropdown
                    options={[
                      { label: tr('FedexProductTerms.signatureOption.DIRECT', 'Direct signature required'), value: 'DIRECT' },
                      { label: tr('FedexProductTerms.signatureOption.INDIRECT', 'Indirect signature allowed'), value: 'INDIRECT' },
                      { label: tr('FedexProductTerms.signatureOption.ADULT', 'Adult signature required'), value: 'ADULT' },
                    ]}
                    optionLabel="label"
                    optionValue="value"
                    value={signatureOption}
                    onChange={e => updateFedexCarriers(item => ({ ...item, _fedexSignatureOption: e.value ?? undefined }))}
                    showClear
                    placeholder={tr('FedexProductTerms.signatureOption.none', 'No preference')}
                    style={{ width: '100%' }}
                  />
                </Field>

                {isFreightService && (
                  <Field label={tr('FedexProductTerms.bookingConfirmation', 'FedEx booking confirmation number')} required errorMessage={bookingError}>
                    <InputText
                      value={bookingConfirmationNumber}
                      onChange={e => updateFedexCarriers(item => ({ ...item, _fedexBookingConfirmationNumber: e.target.value }))}
                    />
                  </Field>
                )}
              </div>
            </>
          )}
          <FedexDisclaimer />
        </div>
      )}
    </>
  );
};
